import { AltRouteOutlined, PersonOutlined } from "@mui/icons-material";
import { Box, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  determinePlatformType,
  isFastDraftDeployment,
} from "src/reusable/Functions";
import { useIsHeaderCompact } from "./AAHeaderButton";
import { useMemo, useState } from "react";

import PersonaDialog from "../../components/persona-list/PersonaDialog";
import { useActivePersonaIdentity, useChatStore } from "../../stores/ChatStore";
import { useGetAllConfigs } from "../../AssistantServices";

import {
  getModeDisplayName,
  getModeMatchSummary,
} from "../../routing/routingDisplayUtils";

const PersonaMetadata = () => {
  const theme = useTheme();
  const { routing } = useChatStore();
  const identity = useActivePersonaIdentity();
  const [open, setOpen] = useState(false);

  const isManuallySelected = identity.source === "manual";

  // Persona name for the chip (not the header — header is always platform name)
  const personaChipName = identity.name;

  // Get raw persona data for the dialog by finding it in the all configs list
  const { data: allConfigs } = useGetAllConfigs({
    searchQuery: "",
    orderBy: "name",
  });
  const fullPersonaData = useMemo(() => {
    if (!identity.slug || !allConfigs) return null;
    return (
      allConfigs.find((config: any) => config.slug === identity.slug) || null
    );
  }, [identity.slug, allConfigs]);

  const isMobile = useIsHeaderCompact();

  // Header always shows platform name — persona details go in the chip below.
  // Same rule as the page title: an IS_FASTDRAFT-only config is FastDraft too,
  // or the tab would read "FastDraft" while this header read "AtomAssist".
  const displayName = isFastDraftDeployment()
    ? "FastDraft"
    : determinePlatformType();

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {/* Title and the routing/persona chip share one row so the chip adds no
          vertical height to the header — it sits beside the name, not under it. */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        sx={{ minWidth: 0 }}
      >
        <Typography
          data-testid="aa-persona-name"
          fontSize={isMobile ? "16px" : "20px"}
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          {displayName}
        </Typography>
        {(routing || isManuallySelected) && (
          <Box sx={{ minWidth: 0, display: "flex" }}>
            {routing ? (
              <Tooltip
                title={
                  <Box sx={{ whiteSpace: "pre-line", fontSize: 12 }}>
                    {routing.access_denied
                      ? routing.restriction_message || "License required"
                      : getModeMatchSummary(routing)}
                  </Box>
                }
                arrow
              >
                <Chip
                  data-testid="aa-routing-chip"
                  label={
                    routing.access_denied
                      ? `${getModeDisplayName(routing)} — license required`
                      : getModeDisplayName(routing)
                  }
                  variant="outlined"
                  size="small"
                  icon={<AltRouteOutlined sx={{ fontSize: 14 }} />}
                  sx={{
                    height: 22,
                    borderColor: routing.access_denied
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                    color: routing.access_denied
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                    "& .MuiChip-icon": {
                      color: routing.access_denied
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
                    },
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Manually selected agent" arrow>
                <Chip
                  label={personaChipName || "Custom"}
                  variant="outlined"
                  size="small"
                  icon={<PersonOutlined sx={{ fontSize: 14 }} />}
                  sx={{
                    height: 22,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    "& .MuiChip-icon": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}
      </Stack>
      {fullPersonaData && (
        <PersonaDialog
          persona={fullPersonaData as any /* Use raw API data structure */}
          open={open}
          setOpen={setOpen}
        />
      )}
    </Box>
  );
};

export default PersonaMetadata;
